import { Link } from "@tanstack/react-router";
import { ArrowUpRightIcon, Clock3Icon, PackageOpenIcon, PinIcon, SearchIcon } from "lucide-react";

import type { AppSummary, AppsResponse, WorkspaceAdminResponse } from "../../../shared/schemas";
import { AppGlyph } from "../../components/AppGlyph";
import { OfflineState } from "../../components/OfflineState";
import { ToolSettingsDialog } from "../../components/ToolSettingsDialog";
import { Button, buttonVariants } from "../../components/ui/button";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "../../components/ui/empty";
import { Skeleton } from "../../components/ui/skeleton";
import { formatLastOpened } from "../../lib/tool-recency";

interface ToolActions {
  isOnline: boolean;
  isPinPending: (appInstanceId: string) => boolean;
  onTogglePin: (app: AppSummary) => void;
}

function ToolPinButton({ app, actions }: { app: AppSummary; actions: ToolActions }) {
  const label = app.pinned ? `Unpin ${app.displayName}` : `Pin ${app.displayName}`;
  return (
    <Button
      aria-label={label}
      aria-pressed={app.pinned}
      className="text-muted-foreground hover:text-primary"
      disabled={!actions.isOnline || actions.isPinPending(app.appInstanceId)}
      size="icon-sm"
      title={label}
      type="button"
      variant="ghost"
      onClick={() => {
        actions.onTogglePin(app);
      }}
    >
      <PinIcon
        aria-hidden="true"
        className={app.pinned ? "fill-current text-primary" : undefined}
      />
    </Button>
  );
}

function ToolCard({
  actions,
  app,
  canManageWorkspace,
  organizationId,
  workspace,
}: {
  actions: ToolActions;
  app: AppSummary;
  canManageWorkspace: boolean;
  organizationId: string;
  workspace: WorkspaceAdminResponse | undefined;
}) {
  return (
    <Card className="pressable-card min-h-52" size="sm">
      <CardHeader>
        <div className="grid size-10 place-items-center rounded-md bg-info-background text-primary">
          <AppGlyph icon={app.icon} />
        </div>
        <CardAction className="flex items-center gap-0.5">
          <ToolPinButton actions={actions} app={app} />
          {canManageWorkspace && workspace ? (
            <ToolSettingsDialog
              isOnline={actions.isOnline}
              organizationId={organizationId}
              tool={app}
              workspace={workspace}
            />
          ) : null}
        </CardAction>
        <CardTitle className="mt-3">{app.displayName}</CardTitle>
        <CardDescription className="line-clamp-3 max-w-md leading-relaxed">
          {app.description}
        </CardDescription>
      </CardHeader>
      <CardContent className="mt-auto flex items-center justify-between gap-3 border-t pt-3">
        <span className="inline-flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
          <Clock3Icon aria-hidden="true" className="size-3.5 shrink-0" />
          {formatLastOpened(app.lastOpenedAt)}
        </span>
        <Link
          className={buttonVariants({
            variant: "ghost",
            size: "sm",
            className: "shrink-0 text-primary",
          })}
          params={{ appInstanceId: app.appInstanceId }}
          search={{ path: "/" }}
          to="/apps/$appInstanceId"
        >
          Open <ArrowUpRightIcon aria-hidden="true" data-icon="inline-end" />
        </Link>
      </CardContent>
    </Card>
  );
}

function PinnedTools({ actions, apps }: { actions: ToolActions; apps: AppSummary[] }) {
  if (apps.length === 0) return null;
  return (
    <section aria-labelledby="pinned-tools-heading" className="mt-8">
      <h2 className="mono-label mb-3" id="pinned-tools-heading">
        Pinned
      </h2>
      <div className="flex flex-wrap gap-3">
        {apps.map((app) => (
          <div
            className="grid min-w-[15rem] flex-1 grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 rounded-lg border bg-card p-3 shadow-card sm:max-w-[21rem]"
            key={app.appInstanceId}
          >
            <div className="grid size-9 place-items-center rounded-md bg-info-background text-primary">
              <AppGlyph icon={app.icon} />
            </div>
            <div className="min-w-0">
              <Link
                className="block truncate font-semibold hover:text-primary"
                params={{ appInstanceId: app.appInstanceId }}
                search={{ path: "/" }}
                to="/apps/$appInstanceId"
              >
                {app.displayName}
              </Link>
              <span className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
                <Clock3Icon aria-hidden="true" className="size-3" />
                {formatLastOpened(app.lastOpenedAt)}
              </span>
            </div>
            <ToolPinButton actions={actions} app={app} />
          </div>
        ))}
      </div>
    </section>
  );
}

function AllTools({
  actions,
  apps,
  canManageWorkspace,
  organizationId,
  workspace,
}: {
  actions: ToolActions;
  apps: AppSummary[];
  canManageWorkspace: boolean;
  organizationId: string;
  workspace: WorkspaceAdminResponse | undefined;
}) {
  return (
    <section aria-labelledby="all-tools-heading" className="mt-9">
      <div className="mb-3 flex items-center justify-between gap-4">
        <h2 className="mono-label m-0" id="all-tools-heading">
          All tools
        </h2>
        <span className="text-xs text-muted-foreground">
          {apps.length} {apps.length === 1 ? "tool" : "tools"}
        </span>
      </div>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {apps.map((app) => (
          <ToolCard
            actions={actions}
            app={app}
            canManageWorkspace={canManageWorkspace}
            key={app.appInstanceId}
            organizationId={organizationId}
            workspace={workspace}
          />
        ))}
      </div>
    </section>
  );
}

function LoadingTools() {
  return (
    <section
      aria-busy="true"
      aria-label="Loading tools"
      className="grid gap-4 py-8 md:grid-cols-2 xl:grid-cols-3"
    >
      {[0, 1, 2].map((item) => (
        <Card className="min-h-52" key={item} size="sm">
          <CardHeader>
            <Skeleton className="size-10" />
            <Skeleton className="mt-3 h-5 w-40" />
            <Skeleton className="h-4 w-full max-w-64" />
          </CardHeader>
          <CardContent className="mt-auto">
            <Skeleton className="h-8 w-full" />
          </CardContent>
        </Card>
      ))}
    </section>
  );
}

function EmptyTools({ kind, search }: { kind: "error" | "empty" | "filtered"; search: string }) {
  const copy =
    kind === "error"
      ? ["These tools couldn’t be loaded.", "Refresh the page to retry the workspace request."]
      : kind === "empty"
        ? [
            "No tools have been assigned yet.",
            "Your consultant can add a purpose-built tool to this workspace.",
          ]
        : [`No tools match “${search.trim()}”.`, "Try a different name or description."];
  return (
    <div className="py-8">
      <Empty {...(kind === "error" ? { role: "alert" } : {})}>
        <EmptyHeader>
          <EmptyMedia variant="icon">
            {kind === "filtered" ? (
              <SearchIcon aria-hidden="true" />
            ) : (
              <PackageOpenIcon aria-hidden="true" />
            )}
          </EmptyMedia>
          <EmptyTitle>{copy[0]}</EmptyTitle>
          <EmptyDescription>{copy[1]}</EmptyDescription>
        </EmptyHeader>
      </Empty>
    </div>
  );
}

export function DashboardTools({
  actions,
  apps,
  canManageWorkspace,
  filteredApps,
  isError,
  isPending,
  organizationId,
  search,
  workspace,
}: {
  actions: ToolActions;
  apps: AppsResponse | undefined;
  canManageWorkspace: boolean;
  filteredApps: AppSummary[];
  isError: boolean;
  isPending: boolean;
  organizationId: string;
  search: string;
  workspace: WorkspaceAdminResponse | undefined;
}) {
  if (isPending && !actions.isOnline) {
    return (
      <div className="py-8">
        <OfflineState
          description="Reconnect to Wi-Fi or cellular data to load the tools assigned to this workspace."
          title="Tools unavailable while offline"
        />
      </div>
    );
  }
  if (isPending) return <LoadingTools />;
  if (isError) return <EmptyTools kind="error" search={search} />;
  if (apps?.apps.length === 0) return <EmptyTools kind="empty" search={search} />;
  if (filteredApps.length === 0) return <EmptyTools kind="filtered" search={search} />;
  return (
    <>
      <PinnedTools actions={actions} apps={filteredApps.filter((app) => app.pinned)} />
      <AllTools
        actions={actions}
        apps={filteredApps}
        canManageWorkspace={canManageWorkspace}
        organizationId={organizationId}
        workspace={workspace}
      />
    </>
  );
}
